import React, { useState, useEffect, useRef } from "react";
import { IoIosSearch } from "react-icons/io";
import { LuPlus } from "react-icons/lu";
import { CiEdit } from "react-icons/ci";
import { IoCloseOutline } from "react-icons/io5";
import axiosInstance from "../axiosinstance";
import PropTypes from "prop-types";
import "./Table.css";
import { useNavigate } from "react-router-dom";
import { CiMenuBurger } from "react-icons/ci";
import { FiInfo } from "react-icons/fi";
import FirmRegistrationForm from "../Components/FirmRegistrationForm"; // adjust path as needed

function SuperAdmin({ isSidebarExpanded }) {    
  const [Firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isDeletePopup, setIsDeletePopup] = useState(false);
  const [isSortPopup, setIsSortPopup] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  // Registration popup state
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [flowMessage, setFlowMessage] = useState("");
  const [flowMessageType, setFlowMessageType] = useState("info");

  // Plan meta — fetched from API (same as SubscriptionPlan)
  const [planMeta, setPlanMeta] = useState({
    plan_id: "",
    plan_name: "Annual Access Plan",
    plan_code: "",
    amount: 0,
    display_amount: 0,
    currency: "INR",
    duration_days: 365,
    max_subsidiaries: 0,
  });
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");

  const containerRef = useRef();
  const navigate = useNavigate();

  const formatDisplayDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatStatusLabel = (status) => {
    if (!status || typeof status !== "string") return "-";
    return status
      .toLowerCase()
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const getPaymentStatusClass = (status) => {
    const normalized = (status || "").toUpperCase();
    if (normalized.includes("PENDING")) return "pending";
    if (normalized.includes("EXPIRED") || normalized.includes("FAILED")) return "failed";
    if (
      normalized.includes("ACTIVE") ||
      normalized.includes("PAID") ||
      normalized.includes("SUCCESS")
    ) {
      return "success";
    }
    return "default";
  };

  const getLocationDetails = (firm) => {
    return [firm?.address_line1, firm?.address_line2, firm?.state]
      .map((value) => (typeof value === "string" ? value.trim() : ""))
      .filter(Boolean);
  };

  // Fetch subscription plan (same logic as SubscriptionPlan.jsx)
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setPlansLoading(true);
        const response = await axiosInstance.get("/api/subscription-plans");
        const plans = Array.isArray(response.data) ? response.data : [];

        if (plans.length === 0) {
          setPlansError("No active plan available.");
          return;
        }

        const selectedPlan = plans[0];
        setPlanMeta({
          plan_id: selectedPlan.plan_id || "",
          plan_name: selectedPlan.plan_name || "Annual Access Plan",
          plan_code: selectedPlan.plan_code || "",
          amount: Number(selectedPlan.amount || 0),
          display_amount: Number(selectedPlan.display_amount || 0),
          currency: selectedPlan.currency || "INR",
          duration_days: Number(selectedPlan.duration_days || 365),
          max_subsidiaries: Number(selectedPlan.max_subsidiaries || 0),
        });
        setPlansError("");
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to load subscription plan.";
        setPlansError(message);
      } finally {
        setPlansLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Fetch firms table data
  const fetchData = async (status = "active") => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        `/api/get_active_firm?page_status=${status}`
      );
      setFirms(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching firm data:", error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData("Active");
  }, []);

  // Open registration popup (same guard as SubscriptionPlan)
  const handleUser = () => {
    if (!planMeta.plan_id) {
      setFlowMessage("Unable to continue: plan is unavailable.");
      setFlowMessageType("error");
      return;
    }
    setFlowMessage("");
    setShowRegistrationForm(true);
  };

  // Called by FirmRegistrationForm on success (same as SubscriptionPlan)
  const handleRegistered = ({ status, firm_id, message }) => {
    setShowRegistrationForm(false);

    if (status === 200) {
      setFlowMessage(message || "Registration already started. Continue payment.");
      setFlowMessageType("info");
    } else {
      setFlowMessage(message || "Firm registered successfully.");
      setFlowMessageType("success");
    }

    fetchData("Active"); // Refresh table
  };

  function handleEdit(id) {
    navigate(`/Edit-Firms/${id}`);
  }

  const handleSearch = async (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      fetchData();
    } else {
      try {
        const response = await axiosInstance.get(`/api/search_firms`, {
          params: { query, page_status: "Active" },
        });
        setFirms(response.data.data || []);
      } catch (error) {
        console.error("Error fetching search results:", error.message);
      }
    }
  };

  const handleCheckboxChange = (firmId) => {
    setSelectedFirms((prevSelected) =>
      prevSelected.includes(firmId)
        ? prevSelected.filter((id) => id !== firmId)
        : [...prevSelected, firmId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFirms([]);
    } else {
      setSelectedFirms(Firms.map((firm) => firm.firm_id));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    setSelectAll(selectedFirms.length === Firms.length && Firms.length > 0);
  }, [selectedFirms, Firms]);

  const handleDeletePopup = () => setIsDeletePopup(true);
  const handleCancel = () => setIsDeletePopup(false);

  const handleArchiveFirms = async () => {
    if (selectedFirms.length === 0) {
      alert("Please select at least one firm to archive.");
      return;
    }

    try {
      const response = await axiosInstance.put(`/api/archive_firm`, {
        ids: selectedFirms,
      });

      if (response.status === 200) {
        alert("Firms archived successfully");
        await fetchData();
        setSelectedFirms([]);
        setSelectAll(false);
      } else {
        alert("Failed to archive firms. Please try again.");
      }
    } catch (error) {
      console.error("Error archiving firms:", error);
      alert("An error occurred while archiving firms.");
    }

    setIsDeletePopup(false);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    const sortedClients = [...Firms].sort((a, b) => {
      const aValue =
        key === "contact_details" && a.contact_details?.length
          ? a.contact_details[0].contact_name.toLowerCase()
          : a[key];
      const bValue =
        key === "contact_details" && b.contact_details?.length
          ? b.contact_details[0].contact_name.toLowerCase()
          : b[key];

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortConfig({ key, direction });
    setFirms(sortedClients);
  };

  const handleSortpopup = () => setIsSortPopup(!isSortPopup);

  const handleArchivedTable = () => navigate("/archived-firms");

  return (
    <div
      className={`table-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      {/* Table Header */}
      <div className="table-header">
        {selectedFirms.length > 0 ? (
          <div className="table-header-content archive-header">
            <button className="delete" onClick={handleDeletePopup}>
              Move to Archive
            </button>
            <button
              className="action-header-close-button"
              onClick={() => setSelectedFirms([])}
            >
              <IoCloseOutline className="action-header-close-icon" />
            </button>
          </div>
        ) : (
          <div className="table-header-content">
            <div className="table-left-side">
              <h2 className="jost-bold">Firms</h2>
            </div>
            <div className="table-right-side">
              <div
                className={`search-icon ${searchVisible ? "no-border" : ""}`}
                onClick={() => setSearchVisible(!searchVisible)}
              >
                <IoIosSearch />
              </div>
              <div
                className="new-button"
                onClick={handleUser}
                title={plansLoading ? "Loading plan..." : plansError || "Add New Firm"}
              >
                <span className="new-icon">
                  <LuPlus />
                </span>
                <span>New</span>
              </div>
              <button className="sort-button" onClick={handleSortpopup}>
                <CiMenuBurger className="sort-icon" />
              </button>
              {isSortPopup && (
                <div className="sort-popup-container" ref={containerRef}>
                  <div className="sort-popup-header">
                    <span className="sort-title">Sort By</span>
                  </div>
                  <div className="sort-popup-content">
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("firm_name")}
                    >
                      Company Name
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("city")}
                    >
                      Location
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("owner_name")}
                    >
                      Owner Name
                    </button>
                    <button
                      className="sort-button sort-button-options"
                      onClick={() => handleSort("max_subsidiaries")}
                    >
                      Max Subsidiaries
                    </button>
                  </div>
                  <button className="delete-item" onClick={handleArchivedTable}>
                    Archived Firms
                  </button>
                </div>
              )}
              <div
                className={`search-bar-container ${
                  searchVisible ? "visible" : "hidden"
                }`}
              >
                <input
                  type="text"
                  placeholder="Search Firms..."
                  value={searchQuery}
                  className="search-bar"
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Flow message (plan error or post-registration status) */}
      {(flowMessage || plansError) && (
        <p className={`subscription-flow-message ${flowMessage ? flowMessageType : "error"}`}>
          {flowMessage || plansError}
        </p>
      )}

      {/* Archive Confirmation Popup */}
      {isDeletePopup && (
        <div className="confirm-delete-popup">
          <div className="confirm-delete-popup-content">
            <div className="confirm-model">
              <div className="confirm-model-content">
                <div className="confirm-model-content-body">
                  <p>Are you sure you want to move to archive?</p>
                </div>
                <div className="confirm-model-content-footer">
                  <button
                    className="confirm-delete-button"
                    onClick={handleArchiveFirms}
                  >
                    Yes
                  </button>
                  <button
                    className="confirm-cancel-button"
                    onClick={handleCancel}
                  >
                    No
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Firm Registration Popup */}
      {showRegistrationForm && (
        <FirmRegistrationForm
          planId={planMeta.plan_id}
          planName={planMeta.plan_name}
          planAmount={planMeta.amount}
          onCancel={() => setShowRegistrationForm(false)}
          onRegistered={handleRegistered}
        />
      )}

      {/* Table Content */}
      <div className="table-container-content form-main-body">
        <div className="data-list-body">
          <div className="data-list-scroll-body">
            <div className="data-list-table">
              {loading ? (
                <div className="loading-spinner">Loading...</div>
              ) : (
                <table className="table multilevel-data">
                  <thead>
                    <tr className="table-row">
                      <th>
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th>Se. No</th>
                      <th>Company Name</th>
                      <th>Location</th>
                      <th>Owner</th>
                      <th>No of Subsidiaries</th>
                      <th>Contact No</th>
                      <th>Email ID</th>
                      <th>Subscription Start</th>
                      <th>Subscription End</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="data-list-scroll-bodys">
                    {Firms.length > 0 ? (
                      Firms.map((firm) => {
                        const locationDetails = getLocationDetails(firm);
                        const locationTooltipId = `location-tooltip-${firm.firm_id}`;

                        return (
                        <tr key={firm.firm_id} className="hoverable-row">
                          <td className="center-align">
                            <input
                              type="checkbox"
                              checked={selectedFirms.includes(firm.firm_id)}
                              onChange={() => handleCheckboxChange(firm.firm_id)}
                            />
                          </td>
                          <td className="center-align">{firm.serial_number}</td>
                          <td className="center-align">{firm.firm_name}</td>
                          <td
                            className="center-align location-cell"
                          >
                            <div className="location-cell-content">
                              <span>{firm.city || "-"}</span>
                              <button
                                type="button"
                                className="location-info-trigger"
                                aria-label="View address details"
                                aria-describedby={locationTooltipId}
                              >
                                <FiInfo aria-hidden="true" />
                              </button>
                              <div
                                className="location-tooltip"
                                role="tooltip"
                                id={locationTooltipId}
                              >
                                {locationDetails.length > 0 ? (
                                  locationDetails.map((line, index) => (
                                    <div key={`${firm.firm_id}-loc-${index}`}>{line}</div>
                                  ))
                                ) : (
                                  <div>No address details available</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="center-align">{firm.owner_name}</td>
                          <td className="center-align">{firm.max_subsidiaries}</td>
                          <td className="center-align">{firm.contact_no}</td>
                          <td className="center-align">{firm.email_id}</td>
                          <td className="center-align">
                            {formatDisplayDate(firm.subscription_start)}
                          </td>
                          <td className="center-align">
                            {formatDisplayDate(firm.subscription_end)}
                          </td>
                          <td className="center-align">
                            <span
                              className={`payment-status-badge ${getPaymentStatusClass(
                                firm.status
                              )}`}
                            >
                              {formatStatusLabel(firm.status)}
                            </span>
                          </td>
                          <td className="center-align">
                            <div
                              className="action-btn"
                              onClick={() => handleEdit(firm.firm_id)}
                            >
                              <span className="action-icon">
                                <CiEdit />
                              </span>
                            </div>
                          </td>
                        </tr>
                      )})
                    ) : (
                      <tr>
                        <td colSpan="12" className="center-align">
                          No firms available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SuperAdmin.propTypes = {
  isSidebarExpanded: PropTypes.bool.isRequired,
};

export default SuperAdmin;
